/**
 * What arrived at the hospital edge nodes, counted exactly by each node's own
 * agent — the Uploads tab's second vantage point on the same flow.
 *
 * Mounted on the uploads report under the center chart, scoped by the same
 * `slug` (or `node`), and on each node's page as a 14-day card
 * (`EdgeNodeUploadsCard`). Four rules shape it:
 *
 * * **Never added to the center numbers.** A study received at the edge is
 *   replicated to its center and counted there again by the center's own
 *   index. The two views are the hospital side and the cloud side of one flow;
 *   a sum of them double-counts every replicated study. This section is its own
 *   card, with its own totals, and says so in words.
 * * **Received and replicated are grouped, never stacked.** Replicated studies
 *   are a subset of received ones — stacking would draw a subset on top of its
 *   own superset and invite exactly the addition the first rule forbids.
 * * **Not reported is not zero.** Counts start the day after a node first runs
 *   agent 1.1.0, and an older agent reports none at all. Such a day is null and
 *   draws nothing; a node on an old agent gets a row that says so rather than a
 *   blank one that reads as a quiet hospital.
 * * **Coverage is a number, not a feeling.** Every total carries how many of
 *   the node-days it should cover actually reported, so "3 of 7 node-days" is
 *   visible instead of hidden inside a smaller total.
 */

import { useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { Badge } from '../Badge';
import { Callout } from '../Callout';
import { SortHeader, TableScroll, useSort, useSortedRows } from '../SortableTable';
import { StatGrid, StatTile } from '../StatTile';
import { ErrorBlock, LoadingBlock } from '../States';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';
import { SegmentedFilter } from '../ui/FilterBar';
import { DownloadIcon } from '../ui/Icon';
import { ChartFrame, ColumnChart, ColumnChartTable } from '../viz/Chart';
import type { Series } from '../viz/Chart';
import { ApiError } from '../../lib/api';
import { addDays, baghdadToday } from '../../lib/baghdad';
import { downloadCsv } from '../../lib/csv';
import type { CsvCell } from '../../lib/csv';
import { formatNumber, pluralise, UNKNOWN } from '../../lib/format';
import { useResource } from '../../lib/useResource';
import type {
  EdgeNodeCounts,
  EdgeUploadPoint,
  EdgeUploadsResponse,
  EdgeUploadTotal,
  UploadBucket,
} from '../../types/api';
import { bucketLabeller, edgeQuery, rangeText } from './edgeReportFormat';

/** The one sentence this data most needs next to it. */
const NEVER_ADD =
  'Replicated studies are already inside the linked center’s uploads — never add the two.';

/** What a mixed-version fleet shows for a node whose agent cannot count. */
const OLD_AGENT = 'agent < 1.1.0 — counts not reported';

type EdgeMeasure = 'studies' | 'instances' | 'frames';

const MEASURE_LABEL: Record<EdgeMeasure, string> = {
  studies: 'Studies',
  instances: 'Instances',
  frames: 'Images (frames)',
};

function receivedOf(p: EdgeUploadPoint | EdgeUploadTotal, measure: EdgeMeasure): number | null {
  return measure === 'studies'
    ? p.received_studies
    : measure === 'instances'
      ? p.received_instances
      : p.received_frames;
}

function replicatedOf(p: EdgeUploadPoint | EdgeUploadTotal, measure: EdgeMeasure): number | null {
  // Frames are not counted as replicated at the edge: a replicated study's
  // frames are the center index's to count, on the Uploads chart above.
  return measure === 'studies'
    ? p.replicated_studies
    : measure === 'instances'
      ? p.replicated_instances
      : null;
}

/**
 * Received vs replicated as two series over the same buckets, for the
 * grouped chart. The fleet-wide response (`group_by=none`) has one point per
 * bucket; a node-scoped one grouped by node has one per bucket too.
 */
function receivedVsReplicated(points: readonly EdgeUploadPoint[], measure: EdgeMeasure): Series[] {
  const series: Series[] = [
    {
      key: 'received',
      label: `${MEASURE_LABEL[measure]} received`,
      slot: 1,
      points: points.map((p) => ({ at: p.bucket, value: receivedOf(p, measure) })),
    },
  ];
  if (measure !== 'frames') {
    series.push({
      key: 'replicated',
      label: `${MEASURE_LABEL[measure]} replicated to the cloud`,
      slot: 2,
      points: points.map((p) => ({ at: p.bucket, value: replicatedOf(p, measure) })),
    });
  }
  return series;
}

function hasFrames(points: readonly EdgeUploadPoint[]): boolean {
  return points.some((p) => p.received_frames !== null);
}

function reportedText(reported: number, expected: number): string {
  return expected === 0
    ? 'no node-days expected yet'
    : `${formatNumber(reported)} of ${formatNumber(expected)} node-days reported`;
}

function csvButton(onClick: () => void, what: string, disabled = false) {
  return (
    <Button kind="ghost" size="sm" renderIcon={DownloadIcon} onClick={onClick} disabled={disabled}>
      CSV<span className="visually-hidden"> of {what}</span>
    </Button>
  );
}

function exportPoints(data: EdgeUploadsResponse, stem: string) {
  const labels = new Map(data.series.map((s) => [s.key ?? '', s.label]));
  const grouped = data.group_by !== 'none';
  downloadCsv(
    `${stem}.csv`,
    [
      'bucket',
      ...(grouped ? ['key', 'label'] : []),
      'received_studies',
      'received_instances',
      'received_frames',
      'replicated_studies',
      'replicated_instances',
      'node_days_reported',
      'node_days_expected',
      'partial',
    ],
    data.points.map((p): CsvCell[] => [
      p.bucket,
      ...(grouped ? [p.key, labels.get(p.key ?? '') ?? p.key] : []),
      p.received_studies,
      p.received_instances,
      p.received_frames,
      p.replicated_studies,
      p.replicated_instances,
      p.node_days_reported,
      p.node_days_expected,
      p.partial,
    ]),
  );
}

// ── the per-node table ───────────────────────────────────────────────────────

interface NodeRow {
  uuid: string;
  label: string;
  hospital: string | null;
  center: string | null;
  agent: string | null;
  countsReported: boolean;
  countsFrom: string | null;
  note: string;
  receivedStudies: number | null;
  receivedInstances: number | null;
  receivedFrames: number | null;
  replicatedStudies: number | null;
  replicatedInstances: number | null;
  reported: number;
  expected: number;
}

type NodeSortKey =
  | 'label'
  | 'receivedStudies'
  | 'receivedInstances'
  | 'replicatedStudies'
  | 'replicatedInstances'
  | 'reported';

function nodeAccessor(row: NodeRow, key: NodeSortKey): unknown {
  // An old agent's nulls sort last in both directions (the table's rule), so
  // "counts not reported" never sorts as the quietest hospital.
  return row[key];
}

function buildNodeRows(
  nodes: readonly EdgeNodeCounts[],
  totals: readonly EdgeUploadTotal[],
): NodeRow[] {
  const byKey = new Map(totals.map((t) => [t.key ?? '', t]));
  return nodes.map((n) => {
    const t = byKey.get(n.node_uuid);
    return {
      uuid: n.node_uuid,
      label: n.label || n.node_uuid,
      hospital: n.hospital,
      center: n.center_slug,
      agent: n.agent_version,
      countsReported: n.counts_reported,
      countsFrom: n.counts_from,
      note: n.note,
      receivedStudies: t?.received_studies ?? null,
      receivedInstances: t?.received_instances ?? null,
      receivedFrames: t?.received_frames ?? null,
      replicatedStudies: t?.replicated_studies ?? null,
      replicatedInstances: t?.replicated_instances ?? null,
      reported: t?.node_days_reported ?? 0,
      expected: t?.node_days_expected ?? 0,
    };
  });
}

// ── the section ──────────────────────────────────────────────────────────────

export interface EdgeUploadsSectionProps {
  /** Inclusive Baghdad days, as the page shows them. */
  fromDay: string;
  toDay: string;
  bucket: UploadBucket;
  /** The page's scope. A node wins over a slug (the server takes one). */
  slug?: string | null;
  node?: string | null;
  /** How the page names the scope ("the whole fleet", a center's name). */
  scopeLabel?: string;
  /** False while the page's window is invalid: no request is made. */
  ready?: boolean;
}

/**
 * Two cards for the uploads report's bento: the chart with its headline, and
 * the per-node table. Returned as a fragment so each card takes its own grid
 * row like the page's other cards.
 */
export function EdgeUploadsSection({
  fromDay,
  toDay,
  bucket,
  slug = null,
  node = null,
  scopeLabel,
  ready = true,
}: EdgeUploadsSectionProps) {
  const [measure, setMeasure] = useState<EdgeMeasure>('studies');

  const scope = { slug, node };
  const path = (groupBy: 'none' | 'node') =>
    `/api/reports/edge-uploads?${edgeQuery({ from: fromDay, to: toDay, bucket, group_by: groupBy }, scope)}`;

  // One request for the fleet-wide series and one grouped by node for the
  // table's totals — the server's arithmetic both times, never a client-side
  // sum across nodes that would have to re-decide what a null means.
  const chart = useResource<EdgeUploadsResponse>(path('none'), { enabled: ready });
  const byNode = useResource<EdgeUploadsResponse>(path('node'), { enabled: ready });

  // On an error (a 409/404 after a scope change) useResource keeps the
  // previous scope's data; never show it under the new scope's label.
  const data = chart.error ? null : chart.data;
  const byNodeData = byNode.error ? null : byNode.data;
  const points = useMemo(() => data?.points ?? [], [data]);
  const framesKnown = hasFrames(points);
  const activeMeasure: EdgeMeasure = measure === 'frames' && !framesKnown ? 'studies' : measure;
  const series = useMemo(
    () => receivedVsReplicated(points, activeMeasure),
    [points, activeMeasure],
  );
  const partial = useMemo(
    () => new Set(points.filter((p) => p.partial).map((p) => p.bucket)),
    [points],
  );
  const total = data?.totals[0] ?? null;

  const nodeRows = useMemo(
    () => buildNodeRows(byNodeData?.nodes ?? [], byNodeData?.totals ?? []),
    [byNodeData],
  );
  const sort = useSort<NodeSortKey>('receivedStudies', 'desc');
  const sortedNodes = useSortedRows(nodeRows, sort, nodeAccessor);
  const oldAgents = nodeRows.filter((r) => !r.countsReported).length;
  const exactSince = useMemo(() => {
    const days = nodeRows.map((r) => r.countsFrom).filter((d): d is string => Boolean(d));
    return days.length > 0 ? [...days].sort()[0]! : null;
  }, [nodeRows]);

  const dateText = rangeText(fromDay, toDay);
  const where = scopeLabel ?? (node ? 'this node' : slug ? slug : 'the whole fleet');
  const stem = (what: string) =>
    `edge-uploads-${node || slug || 'fleet'}-${what}-${fromDay}-to-${toDay}`;
  const labelBucket = bucketLabeller(bucket);
  const format = (value: number) => formatNumber(Math.round(value));

  const exportNodes = () =>
    downloadCsv(
      `${stem('nodes')}.csv`,
      [
        'node_uuid',
        'label',
        'hospital',
        'center_slug',
        'agent_version',
        'counts_reported',
        'counts_from',
        'received_studies',
        'received_instances',
        'received_frames',
        'replicated_studies',
        'replicated_instances',
        'node_days_reported',
        'node_days_expected',
        'note',
      ],
      sortedNodes.map((r): CsvCell[] => [
        r.uuid,
        r.label,
        r.hospital,
        r.center,
        r.agent,
        r.countsReported,
        r.countsFrom,
        r.receivedStudies,
        r.receivedInstances,
        r.receivedFrames,
        r.replicatedStudies,
        r.replicatedInstances,
        r.reported,
        r.expected,
        r.countsReported ? r.note : r.note || OLD_AGENT,
      ]),
    );

  const body = (render: () => ReactNode, resource: typeof chart, what: string): ReactNode => {
    if (!ready) return <p className="viz__empty">Choose a valid window.</p>;
    if (resource.loading) return <LoadingBlock label={`Loading ${what}…`} />;
    if (resource.error instanceof ApiError && resource.error.status === 409)
      return (
        <p className="viz__empty">
          {resource.error.detail} — no edge node can be linked to this center.
        </p>
      );
    if (resource.error)
      return <ErrorBlock what={what} error={resource.error} onRetry={resource.reload} />;
    return render();
  };

  return (
    <>
      <Card className="bento__full" aria-labelledby="edge-uploads-heading">
        <div className="card__body stack">
          <div>
            <h2 className="section__title" id="edge-uploads-heading">
              Arrived at hospital edge nodes
            </h2>
            <p className="muted">
              Exact counts from each node&rsquo;s own agent, by Baghdad day. Counting
              starts the day after a node first runs agent 1.1.0. <strong>{NEVER_ADD}</strong>
            </p>
          </div>

          {data?.truncated ? (
            <Callout tone="warning" title="Edge series truncated">
              Only the most recent buckets are shown
              {points[0] ? <> — from {points[0].bucket}</> : null}. The oldest part of the
              window is missing, not empty. Choose a coarser bucket or a shorter window.
            </Callout>
          ) : null}

          <StatGrid>
            <StatTile
              label="Studies received"
              value={formatNumber(total?.received_studies)}
              hint={`at the edge, ${where}, ${dateText}`}
            />
            <StatTile
              label="Instances received"
              value={formatNumber(total?.received_instances)}
              hint="same window"
            />
            <StatTile
              label="Studies replicated"
              value={formatNumber(total?.replicated_studies)}
              hint="confirmed in the cloud — already in the center counts"
            />
            <StatTile
              label="Coverage"
              value={
                data
                  ? data.node_days_expected === 0
                    ? UNKNOWN
                    : `${Math.round((100 * data.node_days_reported) / data.node_days_expected)}%`
                  : UNKNOWN
              }
              tone={
                data && data.node_days_expected > 0 && data.node_days_reported < data.node_days_expected
                  ? 'warning'
                  : undefined
              }
              hint={
                data
                  ? `${reportedText(data.node_days_reported, data.node_days_expected)}${
                      exactSince ? ` · exact since ${exactSince}` : ''
                    }`
                  : 'node-days with counts'
              }
            />
          </StatGrid>

          <ChartFrame
            title={`${MEASURE_LABEL[activeMeasure]} received vs replicated, per ${bucket}`}
            subtitle={
              <>
                {dateText}. Side by side, not stacked: replicated is part of received.
                A faded column is still counting; an empty slot was not reported, a flat
                tick is a counted zero.
              </>
            }
            series={series}
            unavailable={data?.unavailable_reason || undefined}
            coverage={
              data ? (
                <>
                  {reportedText(data.node_days_reported, data.node_days_expected)}
                  {data.coverage.earliest ? (
                    <>; edge record from {data.coverage.earliest}</>
                  ) : null}
                  .{data.coverage.reason ? <> {data.coverage.reason}</> : null}
                  {oldAgents > 0 ? (
                    <>
                      {' '}
                      {formatNumber(oldAgents)} {pluralise(oldAgents, 'node')} on an agent older
                      than 1.1.0 {oldAgents === 1 ? 'reports' : 'report'} no counts.
                    </>
                  ) : null}
                </>
              ) : undefined
            }
            table={
              data ? (
                <ColumnChartTable
                  series={series}
                  mode="grouped"
                  partial={partial}
                  format={format}
                  bucketLabel={labelBucket}
                  label="Edge uploads per bucket"
                />
              ) : undefined
            }
          >
            {body(
              () => (
                <ColumnChart
                  series={series}
                  mode="grouped"
                  partial={partial}
                  format={format}
                  bucketLabel={labelBucket}
                  label={`${MEASURE_LABEL[activeMeasure]} received and replicated at the edge per ${bucket}, ${dateText}`}
                />
              ),
              chart,
              'the edge upload counts',
            )}
          </ChartFrame>

          <div className="row">
            <SegmentedFilter<EdgeMeasure>
              legend="Edge measure"
              value={activeMeasure}
              onChange={setMeasure}
              options={[
                { value: 'studies', label: 'Studies' },
                { value: 'instances', label: 'Instances' },
                // Only where an agent actually counts frames: an option that
                // can only ever draw an empty chart is noise.
                ...(framesKnown ? [{ value: 'frames' as const, label: 'Images (frames)' }] : []),
              ]}
            />
            <span className="push">
              {csvButton(() => data && exportPoints(data, stem('series')), 'the edge chart', !data)}
            </span>
          </div>
        </div>
      </Card>

      <Card className="bento__full" aria-labelledby="edge-nodes-heading">
        <div className="card__body stack">
          <ChartFrame
            title="By edge node"
            subtitle={`${dateText}. Studies and instances as each node's agent counted them. ${NEVER_ADD}`}
            unavailable={byNodeData?.unavailable_reason || undefined}
          >
            {body(
              () =>
                nodeRows.length === 0 ? (
                  <p className="viz__empty">No edge node is enrolled in this scope.</p>
                ) : (
                  <TableScroll label="Edge uploads by node">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <SortHeader columnKey="label" sort={sort}>Node</SortHeader>
                          <th scope="col">Center</th>
                          <th scope="col">Agent</th>
                          <SortHeader columnKey="receivedStudies" sort={sort} numeric>
                            Studies received
                          </SortHeader>
                          <SortHeader columnKey="receivedInstances" sort={sort} numeric>
                            Instances received
                          </SortHeader>
                          <SortHeader columnKey="replicatedStudies" sort={sort} numeric>
                            Studies replicated
                          </SortHeader>
                          <SortHeader columnKey="replicatedInstances" sort={sort} numeric>
                            Instances replicated
                          </SortHeader>
                          <SortHeader columnKey="reported" sort={sort} numeric>
                            Days reported
                          </SortHeader>
                        </tr>
                      </thead>
                      <tbody>
                        {sortedNodes.map((r) => (
                          <tr key={r.uuid}>
                            <td>
                              <Link to={`/edge-nodes/${encodeURIComponent(r.uuid)}`}>{r.label}</Link>
                              {r.hospital ? <span className="muted"> · {r.hospital}</span> : null}
                            </td>
                            <td>
                              {r.center ? (
                                <Link to={`/centers/${r.center}`}>{r.center}</Link>
                              ) : (
                                <span className="muted">{UNKNOWN}</span>
                              )}
                            </td>
                            <td>
                              {r.agent ? (
                                <span className="mono">{r.agent}</span>
                              ) : (
                                <span className="muted">{UNKNOWN}</span>
                              )}
                            </td>
                            {r.countsReported ? (
                              <>
                                <td className="numeric">{formatNumber(r.receivedStudies)}</td>
                                <td className="numeric">{formatNumber(r.receivedInstances)}</td>
                                <td className="numeric">{formatNumber(r.replicatedStudies)}</td>
                                <td className="numeric">{formatNumber(r.replicatedInstances)}</td>
                                <td className="numeric" title={r.countsFrom ? `counting since ${r.countsFrom}` : undefined}>
                                  {r.expected === 0
                                    ? UNKNOWN
                                    : `${formatNumber(r.reported)} of ${formatNumber(r.expected)}`}
                                </td>
                              </>
                            ) : (
                              // A whole-row statement, not five dashes: the
                              // blanks are the agent's limit, not a quiet night.
                              <td colSpan={5}>
                                <Badge tone="neutral" small title={r.note || undefined}>
                                  {OLD_AGENT}
                                </Badge>
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </TableScroll>
                ),
              byNode,
              'the per-node edge counts',
            )}
          </ChartFrame>
          <div className="row">
            <span className="push">
              {csvButton(exportNodes, 'the per-node edge table', nodeRows.length === 0)}
            </span>
          </div>
        </div>
      </Card>
    </>
  );
}

// ── the node page's card ─────────────────────────────────────────────────────

/** Days the node page shows: two weeks is what an agent re-sends each beat. */
const NODE_CARD_DAYS = 14;

/**
 * "Exact uploads — last 14 days" on one node's page: its received vs
 * replicated studies per Baghdad day, from the same report the Uploads tab
 * reads. A card, not a section, so it sits in the page's masonry.
 */
export function EdgeNodeUploadsCard({ nodeUuid }: { nodeUuid: string }) {
  const today = baghdadToday();
  const from = addDays(today, -(NODE_CARD_DAYS - 1));
  const report = useResource<EdgeUploadsResponse>(
    `/api/reports/edge-uploads?${edgeQuery(
      { from, to: today, bucket: 'day', group_by: 'node' },
      { node: nodeUuid },
    )}`,
  );

  const data = report.error ? null : report.data;
  const points = useMemo(() => data?.points ?? [], [data]);
  const series = useMemo(() => receivedVsReplicated(points, 'studies'), [points]);
  const partial = useMemo(
    () => new Set(points.filter((p) => p.partial).map((p) => p.bucket)),
    [points],
  );
  const counts = data?.nodes.find((n) => n.node_uuid === nodeUuid) ?? null;
  const total = data?.totals.find((t) => t.key === nodeUuid) ?? data?.totals[0] ?? null;
  const labelBucket = bucketLabeller('day');
  const format = (value: number) => formatNumber(Math.round(value));

  return (
    <Card aria-labelledby="edge-exact-heading">
      <div className="card__header">
        <h2 className="section__title" id="edge-exact-heading">
          Exact uploads — last {NODE_CARD_DAYS} days
        </h2>
        <span className="section__count">Baghdad days</span>
      </div>
      <div className="card__body stack">
        {report.loading ? (
          <LoadingBlock label="Loading this node's counts…" />
        ) : report.error instanceof ApiError && report.error.status === 409 ? (
          <p className="viz__empty">
            {report.error.detail} — no edge node can be linked to this center.
          </p>
        ) : report.error ? (
          <ErrorBlock what="this node's counts" error={report.error} onRetry={report.reload} />
        ) : data?.unavailable_reason ? (
          <Callout tone="warning" title="Edge counts are not available">
            {data.unavailable_reason}
          </Callout>
        ) : counts && !counts.counts_reported ? (
          <Callout tone="info" title="This node does not report counts">
            Its agent{counts.agent_version ? <> (<code>{counts.agent_version}</code>)</> : null}{' '}
            predates 1.1.0, so there are no per-day counts to show — which is not the same as
            nothing arriving. Counting starts the day after the node runs agent 1.1.0.
          </Callout>
        ) : (
          <>
            <ChartFrame
              title="Studies received vs replicated"
              subtitle="Side by side: replicated is part of received. An empty slot was not reported; a flat tick is a counted zero."
              series={series}
              table={
                <ColumnChartTable
                  series={series}
                  mode="grouped"
                  partial={partial}
                  format={format}
                  bucketLabel={labelBucket}
                  label="This node's studies per day"
                />
              }
            >
              <ColumnChart
                series={series}
                mode="grouped"
                partial={partial}
                format={format}
                bucketLabel={labelBucket}
                label={`Studies received and replicated per day, last ${NODE_CARD_DAYS} days`}
              />
            </ChartFrame>
            <p className="muted">
              {formatNumber(total?.received_studies)} studies ·{' '}
              {formatNumber(total?.received_instances)} instances received;{' '}
              {formatNumber(total?.replicated_studies)} studies replicated
              {total ? <> · {reportedText(total.node_days_reported, total.node_days_expected)}</> : null}
              {counts?.counts_from ? <> · counting since {counts.counts_from}</> : null}. The
              replicated ones are already in the center&rsquo;s uploads — never add the two.
            </p>
          </>
        )}
      </div>
    </Card>
  );
}
